import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { addressService } from '../services/addressService';
import AddressModal from '../components/Address/AddressModal';

const AddressesPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const data = await addressService.getAll();
      setAddresses(data || []);
    } catch (err) {
      console.error("Failed to load addresses", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true });
      return;
    }
    fetchAddresses();
  }, [isLoggedIn, navigate]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this address?")) {
      try {
        await addressService.delete(id);
        fetchAddresses();
      } catch (err) {
        alert("Failed to delete address");
      }
    }
  };

  const handleSetPrimary = async (address) => {
    try {
      await addressService.update(address.id, { ...address, isPrimary: true });
      fetchAddresses();
    } catch (err) {
      alert("Failed to set as default");
    }
  };

  const openAddModal = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const openEditModal = (address) => {
    setEditData(address);
    setIsModalOpen(true);
  };

  return (
    <div className="py-6 px-4 md:px-8 max-w-5xl mx-auto w-full min-h-[70vh]">
      <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-5">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
            <i className="fas fa-map-marker-alt text-[#FF7A59]"></i> My Addresses
          </h1>
        </div>
        <button onClick={openAddModal} className="bg-[#2CCE75] hover:bg-[#1EA85E] text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-md flex items-center gap-2">
          <i className="fas fa-plus"></i> <span className="hidden sm:inline">Add New</span>
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
          <p className="text-gray-500 font-medium">Loading addresses...</p>
        </div>
      ) : addresses.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
          <div className="w-24 h-24 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <i className="fas fa-address-book text-4xl"></i>
          </div>
          <h3 className="text-2xl text-gray-800 font-black mb-2">No Saved Addresses</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-8">You haven't saved any delivery addresses yet. Add one now to speed up checkout.</p>
          <button onClick={openAddModal} className="bg-[#0F2F63] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#1a4a8a] transition-all shadow-md">
            Add Address
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {addresses.map((addr) => (
            <div key={addr.id} className={`bg-white p-5 rounded-2xl border-2 transition-all shadow-sm ${addr.isPrimary ? 'border-[#2CCE75] bg-emerald-50/10' : 'border-gray-100 hover:border-gray-200'}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-lg text-xs font-black uppercase tracking-widest flex items-center gap-1.5">
                    <i className={`fas ${addr.addressType === 'HOME' ? 'fa-home' : addr.addressType === 'WORK' ? 'fa-briefcase' : 'fa-map-marker-alt'}`}></i> {addr.addressType}
                  </span>
                  {addr.isPrimary && <span className="bg-emerald-100 text-emerald-600 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest">Default</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEditModal(addr)} className="w-8 h-8 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center hover:bg-sky-100 transition-colors" title="Edit">
                    <i className="fas fa-pen text-xs"></i>
                  </button>
                  <button onClick={() => handleDelete(addr.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors" title="Delete">
                    <i className="fas fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
              <h4 className="text-base font-bold text-gray-900 mb-1">{addr.receiverName}</h4>
              <p className="text-sm text-gray-500 font-medium mb-3 min-h-[40px]">
                {addr.houseNumber}, {addr.apartmentName && `${addr.apartmentName}, `} {addr.streetName}, {addr.landMark && `Near ${addr.landMark}, `} {addr.city}, {addr.state} - {addr.pinCode}
              </p>
              <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                <p className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <i className="fas fa-phone-alt text-gray-400"></i> {addr.phoneNumber}
                </p>
                {!addr.isPrimary && (
                  <button onClick={() => handleSetPrimary(addr)} className="text-xs font-bold text-sky-600 hover:text-sky-800 transition-colors">
                    Set as Default
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddressModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSuccess={fetchAddresses} 
        initialData={editData} 
      />
    </div>
  );
};

export default AddressesPage;